<template>
  <Teleport to="body">
    <div v-if="show" class="cms-modal-overlay" @click="closeModal">
      <div class="cms-modal cms-modal-large" @click.stop>
        <div class="cms-modal-header">
          <h3>{{ $t('cms.storyCategories.title') }}</h3>
          <button type="button" class="cms-modal-close-btn" @click="closeModal" :aria-label="$t('general.close')">
            <v-icon icon="close" size="md" />
          </button>
        </div>

        <div class="cms-modal-body">
          <!-- Existing Categories List (tree view) -->
          <div class="cms-categories-list-section">
            <h4>{{ $t('cms.storyCategories.existingCategories') }}</h4>
            <div v-if="isLoading" class="cms-loading-spinner"></div>
            <div v-else-if="flatCategories.length === 0" class="cms-empty-state">
              <p>{{ $t('cms.storyCategories.noCategories') }}</p>
            </div>
            <div v-else class="cms-story-category-tree">
              <div
                v-for="item in renderTree(flatCategories, null)"
                :key="item.id"
                class="cms-story-category-item"
                :style="{ paddingLeft: `${(item.depth || 0) * 20}px` }"
              >
                <span class="cms-story-category-name">
                  <v-icon v-if="item.children?.length" :icon="expandedIds.has(item.id) ? 'chevron-down' : 'chevron-right'" size="sm" class="cms-tree-toggle" @click="toggleExpand(item.id)" />
                  <span v-else class="cms-tree-spacer"></span>
                  <span class="cms-type-badge" :class="item.type">{{ item.type }}</span>
                  {{ item.name }}
                </span>
                <div class="cms-category-actions">
                  <cms-icon-button
                    variant="primary"
                    icon="pencil"
                    size="sm"
                    :disabled="isEditing || isDeleting === item.id"
                    @click="startEditCategory(item)"
                  />
                  <cms-icon-button
                    variant="danger"
                    icon="trash"
                    size="sm"
                    :disabled="isEditing || isDeleting === item.id"
                    @click="deleteCategory(item)"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Add/Edit Category Form -->
          <div class="cms-category-form">
            <h4>{{ isEditing ? $t('cms.storyCategories.editCategory') : $t('cms.storyCategories.addNew') }}</h4>
            <form @submit.prevent="isEditing ? updateCategory() : createCategory()">
              <div class="cms-form-grid">
                <div class="cms-form-group">
                  <label>{{ $t('cms.storyCategories.type') }} <span class="cms-required">*</span></label>
                  <select v-model="newCategory.type" class="cms-input" required>
                    <option value="destination">{{ $t('cms.storyCategories.types.destination') }}</option>
                    <option value="topic">{{ $t('cms.storyCategories.types.topic') }}</option>
                  </select>
                </div>
                <div class="cms-form-group">
                  <label>{{ $t('cms.storyCategories.parent') }}</label>
                  <select v-model="parentSelectValue" class="cms-input">
                    <option value="">{{ $t('cms.storyCategories.rootCategory') }}</option>
                    <option
                      v-for="cat in parentOptions"
                      :key="cat.id"
                      :value="cat.id"
                      :disabled="editingCategoryId === cat.id"
                    >
                      {{ '—'.repeat(cat.depth || 0) }} {{ cat.name }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="cms-form-grid">
                <div class="cms-form-group">
                  <label>{{ $t('cms.storyCategories.slug') }} <span class="cms-required">*</span></label>
                  <input
                    v-model="newCategory.slug"
                    type="text"
                    class="cms-input"
                    :placeholder="$t('cms.storyCategories.slugPlaceholder')"
                    required
                  />
                </div>
                <div class="cms-form-group">
                  <label>{{ $t('cms.storyCategories.order') }}</label>
                  <input v-model.number="newCategory.order" type="number" class="cms-input" min="0" />
                </div>
              </div>

              <cms-translation-tabs :languages="languages" v-model="currentLanguage">
                <template #default="{ locale }">
                  <div class="cms-form-group">
                    <label>
                      {{ $t('cms.storyCategories.name') }} <span class="cms-required">*</span>
                      ({{ locale.toUpperCase() }})
                    </label>
                    <input
                      v-model="newCategory.translations[locale].name"
                      type="text"
                      class="cms-input"
                      required
                    />
                  </div>
                </template>
              </cms-translation-tabs>

              <div class="cms-form-group">
                <label class="cms-checkbox-item">
                  <input v-model="newCategory.is_public" type="checkbox" class="cms-checkbox" />
                  <span>{{ $t('cms.storyCategories.isPublic') }}</span>
                </label>
              </div>

              <div class="cms-modal-actions">
                <button v-if="isEditing" type="button" class="cms-btn cms-btn-secondary" @click="cancelEdit">
                  {{ $t('general.button.cancel') }}
                </button>
                <button type="button" class="cms-btn cms-btn-secondary" @click="resetNewCategory">
                  {{ $t('cms.storyCategories.reset') }}
                </button>
                <button type="submit" class="cms-btn cms-btn-primary" :disabled="isCreating || isUpdating">
                  {{ (isCreating || isUpdating) ? $t('general.button.saving') : (isEditing ? $t('general.button.save') : $t('general.button.add')) }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import CmsTranslationTabs from '@/components/cms/CmsTranslationTabs.vue';
import CmsIconButton from '@/components/cms/CmsIconButton.vue';
import api from '@/api';
import { useAuthStore } from '@/stores/auth';
import useConcepts from '@/helpers/Concepts';
import { useAppLanguages } from '@/composables/useAppLanguages';
import { normalizeResource } from '@/composables/useCmsActivityData';

interface StoryCategory {
  id: number;
  name: string;
  slug: string;
  type: string;
  parent_id: number | null;
  order: number;
  is_public: boolean;
  depth?: number;
  children?: StoryCategory[];
}

function extractListData(data: any): any[] {
  if (Array.isArray(data)) return data;
  if (data?.data) return Array.isArray(data.data) ? data.data : [data.data];
  if (data?.attributes) return [data];
  return [];
}

export default defineComponent({
  name: 'CmsStoryCategoryModal',
  components: {
    VIcon,
    CmsTranslationTabs,
    CmsIconButton,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'category-created', 'category-updated', 'category-deleted'],
  setup(props, { emit }) {
    const toast = useToast();
    const { t } = useI18n();
    const authStore = useAuthStore();
    const concepts = useConcepts();

    const agencySlug = computed(() => authStore.agency?.slug ?? null);
    const { languages: appLanguages, locales, resolveStoredLocale } = useAppLanguages();

    const buildEmptyCategoryTranslations = () =>
      Object.fromEntries(locales.value.map((locale) => [locale, { name: '' }]));
    const languages = computed(() =>
      appLanguages.value.map((lang: any) => ({
        value: lang.value || lang.key,
        label: lang.label || lang.name,
        key: lang.key || lang.value,
        shortName: lang.shortName || lang.value || lang.key,
      })),
    );
    const currentLanguage = ref<string>(resolveStoredLocale(localStorage.getItem('language')));

    const flatCategories = ref<StoryCategory[]>([]);
    const isLoading = ref(false);
    const isCreating = ref(false);
    const isUpdating = ref(false);
    const isDeleting = ref<number | null>(null);
    const isEditing = ref(false);
    const editingCategoryId = ref<number | null>(null);
    const expandedIds = ref<Set<number>>(new Set());

    const newCategory = reactive<{
      slug: string;
      order: number;
      type: string;
      parent_id: number | null;
      is_public: boolean;
      translations: Record<string, { name: string }>;
    }>({
      slug: '',
      order: 0,
      type: 'destination',
      parent_id: null,
      is_public: true,
      translations: buildEmptyCategoryTranslations(),
    });

    watch(locales, () => {
      const empty = buildEmptyCategoryTranslations();
      newCategory.translations = {
        ...empty,
        ...newCategory.translations,
      };
    });

    const closeModal = () => {
      emit('close');
    };

    const loadCategories = async () => {
      if (!agencySlug.value) return;

      isLoading.value = true;
      try {
        const response = await api.storyCategoryService.listCmsStoryCategories(agencySlug.value, {
          filters: { as_tree: false },
        });

        const list = extractListData(response);
        const items = Array.isArray(list) ? list : [list].filter(Boolean);

        flatCategories.value = items.map((cat: any) => {
          const normalized = normalizeResource(cat) || cat;
          const translations = extractListData(normalized.translations || []);
          const translation =
            translations.find((t: any) => (t.locale || '').toLowerCase() === currentLanguage.value.toLowerCase()) ||
            translations[0] ||
            {};
          const rawParentId = normalized.parent_id ?? normalized.parentId ?? normalized.attributes?.parent_id ?? null;

          return {
            id: normalized.id ?? normalized.attributes?.id,
            name: translation.name ?? normalized.name ?? '',
            slug: normalized.slug ?? normalized.attributes?.slug ?? '',
            type: normalized.type ?? normalized.attributes?.type ?? 'destination',
            parent_id: rawParentId != null ? Number(rawParentId) : null,
            order: normalized.order ?? normalized.attributes?.order ?? 0,
            is_public: normalized.is_public ?? normalized.attributes?.is_public ?? true,
          };
        });
      } catch (error: any) {
        console.error('Failed to load story categories', error);
        toast.error(error?.response?.data?.message || t('general.errorMessage'));
      } finally {
        isLoading.value = false;
      }
    };

    const parentSelectValue = computed({
      get: () => (newCategory.parent_id === null || newCategory.parent_id === undefined ? '' : String(newCategory.parent_id)),
      set: (val) => {
        if (val === '' || val === null || val === undefined) {
          newCategory.parent_id = null;
          return;
        }
        const parsed = Number(val);
        newCategory.parent_id = Number.isNaN(parsed) ? null : parsed;
      },
    });

    const parentOptions = computed(() => {
      const options: { id: number; name: string; depth: number }[] = [];
      const addOptions = (items: StoryCategory[], parentId: number | null, depth: number) => {
        for (const item of items) {
          if (item.parent_id == parentId && item.type === newCategory.type) {
            if (item.id !== editingCategoryId.value) {
              options.push({ id: item.id, name: item.name, depth });
              addOptions(items, item.id, depth + 1);
            }
          }
        }
      };
      addOptions(flatCategories.value, null, 0);
      return options;
    });

    const renderTree = (items: StoryCategory[], parentId: number | null, depth = 0): StoryCategory[] => {
      const result: StoryCategory[] = [];
      for (const item of items) {
        if (item.parent_id == parentId) {
          const children = renderTree(items, item.id, depth + 1);
          const node = { ...item, depth, children };
          result.push(node);
          if (children.length > 0 && expandedIds.value.has(item.id)) {
            result.push(...children);
          }
        }
      }
      return result;
    };

    const toggleExpand = (id: number) => {
      const next = new Set(expandedIds.value);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      expandedIds.value = next;
    };

    const resetNewCategory = () => {
      newCategory.slug = '';
      newCategory.order = 0;
      newCategory.type = 'destination';
      newCategory.parent_id = null;
      newCategory.is_public = true;
      newCategory.translations = buildEmptyCategoryTranslations();
      isEditing.value = false;
      editingCategoryId.value = null;
    };

    const cancelEdit = () => {
      resetNewCategory();
    };

    const startEditCategory = async (category: StoryCategory) => {
      if (!agencySlug.value) return;

      isEditing.value = true;
      editingCategoryId.value = category.id;

      try {
        const response = await api.storyCategoryService.getCmsStoryCategory(agencySlug.value, category.id);
        const normalized = normalizeResource(response) || response;
        const attrs = normalized.attributes || normalized;
        const translationsList = extractListData(normalized.translations || attrs.translations || []);
        const translationsObj: Record<string, { name: string }> = {};

        languages.value.forEach((lang: any) => {
          const locale = lang.key || lang.value;
          const tr = translationsList.find((t: any) => (t.locale || '').toLowerCase() === locale.toLowerCase());
          translationsObj[locale] = { name: tr?.name ?? attrs.name ?? category.name ?? '' };
        });

        newCategory.slug = attrs.slug ?? normalized.slug ?? category.slug ?? '';
        newCategory.order = attrs.order ?? normalized.order ?? category.order ?? 0;
        newCategory.type = attrs.type ?? normalized.type ?? category.type ?? 'destination';
        newCategory.parent_id = attrs.parent_id ?? normalized.parentId ?? category.parent_id ?? null;
        newCategory.is_public = attrs.is_public ?? normalized.is_public ?? category.is_public ?? true;
        newCategory.translations = translationsObj;
      } catch (error: any) {
        console.error('Failed to load category for editing', error);
        toast.error(error?.response?.data?.message || t('general.errorMessage'));
        cancelEdit();
      }
    };

    const createCategory = async () => {
      if (!agencySlug.value) return;

      isCreating.value = true;
      try {
        const translations = Object.keys(newCategory.translations).map((locale) => ({
          locale,
          name: newCategory.translations[locale as keyof typeof newCategory.translations].name,
        }));

        const payload: any = {
          slug: newCategory.slug,
          order: newCategory.order || 0,
          type: newCategory.type,
          parent_id: newCategory.parent_id,
          is_public: newCategory.is_public,
          translations,
        };

        const response = await api.storyCategoryService.createCmsStoryCategory(agencySlug.value, payload);

        toast.success(t('cms.storyCategories.createSuccess'));
        resetNewCategory();
        await loadCategories();
        emit('category-created', response);
      } catch (error: any) {
        console.error('Failed to create story category', error);
        toast.error(error?.response?.data?.message || t('general.errorMessage'));
      } finally {
        isCreating.value = false;
      }
    };

    const updateCategory = async () => {
      if (!agencySlug.value || !editingCategoryId.value) return;

      isUpdating.value = true;
      try {
        const translations = Object.keys(newCategory.translations).map((locale) => ({
          locale,
          name: newCategory.translations[locale as keyof typeof newCategory.translations].name,
        }));

        const payload: any = {
          slug: newCategory.slug,
          order: newCategory.order || 0,
          type: newCategory.type,
          parent_id: newCategory.parent_id,
          is_public: newCategory.is_public,
          translations,
        };

        const response = await api.storyCategoryService.updateCmsStoryCategory(
          agencySlug.value,
          editingCategoryId.value,
          payload
        );

        toast.success(t('cms.storyCategories.updateSuccess'));
        resetNewCategory();
        await loadCategories();
        emit('category-updated', response);
      } catch (error: any) {
        console.error('Failed to update story category', error);
        toast.error(error?.response?.data?.message || t('general.errorMessage'));
      } finally {
        isUpdating.value = false;
      }
    };

    const deleteCategory = async (category: StoryCategory) => {
      if (!agencySlug.value) return;
      if (!window.confirm(t('general.deleteConfirmation') || 'Are you sure?')) return;

      isDeleting.value = category.id;
      try {
        await api.storyCategoryService.deleteCmsStoryCategory(agencySlug.value, category.id);
        toast.success(t('cms.storyCategories.deleteSuccess'));
        await loadCategories();
        emit('category-deleted', category);
      } catch (error: any) {
        console.error('Failed to delete story category', error);
        toast.error(error?.response?.data?.message || t('general.errorMessage'));
      } finally {
        isDeleting.value = null;
      }
    };

    watch(
      () => props.show,
      (show) => {
        if (show && agencySlug.value) {
          loadCategories();
        }
      }
    );

    return {
      flatCategories,
      isLoading,
      isCreating,
      isUpdating,
      isDeleting,
      isEditing,
      editingCategoryId,
      newCategory,
      languages,
      currentLanguage,
      expandedIds,
      parentOptions,
      parentSelectValue,
      closeModal,
      resetNewCategory,
      cancelEdit,
      createCategory,
      updateCategory,
      startEditCategory,
      deleteCategory,
      renderTree,
      toggleExpand,
    };
  },
});
</script>

<style lang="scss" scoped>
@import './cms-styles.scss';

.cms-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
  overflow-y: auto;
  box-sizing: border-box;
}

.cms-modal {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 800px;
  max-height: calc(100vh - 4rem);
  overflow: hidden;
  color: #333;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  margin: auto;
  position: relative;
  min-height: 200px;
}

.cms-modal-large {
  max-width: 800px;
}

.cms-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
  background: white;
  position: sticky;
  top: 0;
  z-index: 100;
  min-height: 60px;
  box-sizing: border-box;

  h3 {
    margin: 0;
    font-size: 1.3rem;
    font-weight: 600;
    color: #333;
    flex: 1;
    line-height: 1.4;
  }

  .cms-modal-close-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: none;
    background: rgba(0, 0, 0, 0.05);
    color: #666;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;
    margin-left: 1rem;

    &:hover {
      background: rgba(0, 0, 0, 0.1);
      color: #333;
      transform: scale(1.05);
    }

    :deep(.icon) {
      width: 20px;
      height: 20px;
      display: block;
    }
  }
}

.cms-modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  max-height: calc(82vh - 12rem);
}

.cms-category-form {
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 2px solid $cms-border-color;

  h4 {
    margin: 0 0 1.5rem 0;
    font-size: 1.1rem;
    color: $cms-text-primary;
  }
}

.cms-categories-list-section {
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 2px solid $cms-border-color;

  h4 {
    margin: 0 0 1rem 0;
    font-size: 1.1rem;
    color: $cms-text-primary;
  }
}

.cms-story-category-tree {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.cms-story-category-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  background: $cms-background-gray-light;
  border: 1px solid $cms-border-color;
  border-radius: 6px;

  .cms-story-category-name {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .cms-tree-toggle {
      cursor: pointer;
    }

    .cms-tree-spacer {
      display: inline-block;
      width: 16px;
    }

    .cms-type-badge {
      font-size: 0.7rem;
      padding: 0.2rem 0.5rem;
      border-radius: 4px;
      background: #e0e0e0;
      color: #555;

      &.destination {
        background: #e3f2fd;
        color: #1976d2;
      }

      &.topic {
        background: #f3e5f5;
        color: #7b1fa2;
      }
    }
  }
}

.cms-category-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.cms-modal-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #eee;
}
</style>
